import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type React from 'react';

export interface Column<T> {
  id: string;
  label: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string | number;
  render?: (row: T) => React.ReactNode;
  cellSx?: (row: T) => SxProps<Theme>;
  // If render is not provided, it will try to access row[id]
}

export interface StandardTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string | number;
  isLoading?: boolean;
  emptyStateMessage?: React.ReactNode;
  size?: 'small' | 'medium';
  stickyHeader?: boolean;
  maxHeight?: string | number;
  onRowClick?: ((row: T) => void) | undefined;
  getRowTestId?: (row: T) => string;
  rowSx?: (row: T) => SxProps<Theme>;
}

export function StandardTable<T>({
  columns,
  data,
  keyExtractor,
  isLoading = false,
  emptyStateMessage = 'No data available',
  size = 'small',
  stickyHeader = true,
  maxHeight,
  onRowClick,
  getRowTestId,
  rowSx,
}: StandardTableProps<T>) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight }}>
      <Table stickyHeader={stickyHeader} size={size}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                style={{ width: column.width }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {emptyStateMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => (
              <TableRow
                key={keyExtractor(row)}
                hover
                onClick={() => onRowClick?.(row)}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  // biome-ignore lint/suspicious/noExplicitAny: Spreading SxProps requires any
                  ...(rowSx ? (rowSx(row) as any) : {}),
                }}
                data-testid={getRowTestId ? getRowTestId(row) : undefined}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={column.cellSx ? column.cellSx(row) : undefined}
                  >
                    {column.render
                      ? column.render(row)
                      : (row as unknown as Record<string, React.ReactNode>)[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default StandardTable;
